import React, { useEffect, useState } from 'react';
import {
  MagnifyingGlassIcon,
  UserIcon,
  UserGroupIcon,
  ArrowRightOnRectangleIcon,
  ChevronRightIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { EmployeesClient, EmployeeDto } from '../../web-api-client';
import { API_CONFIG } from '../../config/api';
import ImportEmployeesModal from '../../components/employees/ImportEmployeesModal';
import { getOffboardingReasonText } from '../../utils/enumHelpers';
import { formatDate } from '../../utils/dateHelpers';
import { ERROR_MESSAGES, EMPTY_STATE_MESSAGES } from '../../constants/messages';

type TabType = 'active' | 'exited';

export default function Employees() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<EmployeeDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<TabType>('active');
  const [showImportModal, setShowImportModal] = useState(false);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const client = new EmployeesClient(API_CONFIG.baseUrl);
      const isActive = activeTab === 'active';
      const data = await client.getEmployees(null, isActive, null);
      setEmployees(data);
    } catch (error) {
      console.error(ERROR_MESSAGES.FETCH_FAILED('employees'), error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, [activeTab]);

  const handleImportSuccess = () => {
    fetchEmployees();
  };

  const filteredEmployees = employees.filter(employee =>
    employee.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.workEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.personalEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.department?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="mb-6">
          <div className="h-8 bg-gray-200 rounded w-48 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-96"></div>
        </div>
        <div className="bg-white shadow rounded-lg">
          <div className="p-6">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center space-x-4 py-4">
                <div className="h-12 w-12 bg-gray-200 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-48 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-32"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Employees</h1>
            <p className="mt-2 text-sm text-gray-600">
              Manage and track all employees in your organization
            </p>
          </div>
          <button
            onClick={() => setShowImportModal(true)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
            Import Employees
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-sm p-2">
        <div className="flex space-x-1">
          <button
            onClick={() => setActiveTab('active')}
            className={`flex-1 flex items-center justify-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
              activeTab === 'active'
                ? 'bg-blue-600 text-white shadow-lg'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <UserGroupIcon className="h-5 w-5 mr-2" />
            Active Employees
          </button>
          <button
            onClick={() => setActiveTab('exited')}
            className={`flex-1 flex items-center justify-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
              activeTab === 'exited'
                ? 'bg-gray-700 text-white shadow-lg'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2" />
            Exited Employees
          </button>
        </div>
      </div>

      {/* Search and filters */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="max-w-2xl">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search employees by name, email, or department..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            />
          </div>
        </div>
      </div>

      {/* Employee list */}
      <div className="bg-white shadow-sm rounded-2xl overflow-hidden">
        {filteredEmployees.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {activeTab === 'active' ? 'No active employees found' : 'No exited employees found'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm
                ? EMPTY_STATE_MESSAGES.SEARCH_NO_RESULTS
                : EMPTY_STATE_MESSAGES.NO_EMPLOYEES(activeTab)}
            </p>
          </div>
        ) : (
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th scope="col" className="py-3.5 pl-6 pr-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Department
                </th>
                {activeTab === 'active' ? (
                  <>
                    <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Designation
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </>
                ) : (
                  <>
                    <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Join Date
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Type of Exit
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Date of Exit
                    </th>
                  </>
                )}
                <th scope="col" className="relative py-3.5 pl-3 pr-6">
                  <span className="sr-only">View</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredEmployees.map((employee) => (
                <tr
                  key={employee.id}
                  onClick={() => navigate(`/employees/${employee.id}`)}
                  className="hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                >
                  <td className="py-4 pl-6 pr-3">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900">
                        {employee.fullName}
                      </p>
                      <p className="text-sm text-gray-500">
                        {employee.workEmail || employee.personalEmail}
                      </p>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-600">
                    {employee.department || '-'}
                  </td>
                  {activeTab === 'active' ? (
                    <>
                      <td className="px-3 py-4 text-sm text-gray-600">
                        {employee.designation || '-'}
                      </td>
                      <td className="px-3 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-4 text-sm text-gray-600">
                        {formatDate(employee.employmentDate)}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-600">
                        {getOffboardingReasonText(employee.offboardingReason)}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-600">
                        {formatDate(employee.lastWorkingDate)}
                      </td>
                    </>
                  )}
                  <td className="py-4 pl-3 pr-6 text-right">
                    <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination would go here */}
      {filteredEmployees.length > 0 && (
        <div className="mt-6 flex justify-between items-center">
          <p className="text-sm text-gray-700">
            Showing {filteredEmployees.length} of {employees.length} employees
          </p>
        </div>
      )}

      {/* Import Modal */}
      <ImportEmployeesModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={handleImportSuccess}
      />
    </div>
  );
}